/**
 * The phone calendar's arithmetic (Aaron, Oct 25, phone notes 3.1, 3.4, 5–7)
 * — apart from the screen, so the rules can be tested on their own: which
 * days the strip and each view show, which way a finger went, where a held
 * slot's hour lands as the finger drags it, how fast the grid scrolls under
 * a finger at its edge, and how overlapping things share a column.
 *
 * Outlook's phone calendar is the reference: a week strip that swipes a week
 * at a time and opens, from its month button, into a month you can scroll
 * through (the pull-down went — Aaron, Oct 27, note 1.1.3); Day and 3 days
 * as hours with a solid line each hour and a fainter one each half hour; and
 * a touch held on an empty slot that becomes an hour's ghost, dragged with
 * the finger and snapped to **15 minutes**, opening New meeting where it is
 * let go.
 */
import { addDays, daysBetween, weekStartOf, type PhoneCalendarView } from '@rowted/shared'

const DAY_MINUTES = 24 * 60

// ---------------------------------------------------------------------------
// Which days
// ---------------------------------------------------------------------------

/** The days a view draws as columns: Day one, 3 days the selected day and the two after. */
export function phoneDays(view: PhoneCalendarView, date: string): string[] {
  return view === 'three' ? [date, addDays(date, 1), addDays(date, 2)] : [date]
}

/** The week strip's seven days, Sunday first (§6.1). */
export function stripWeek(date: string): string[] {
  const sunday = weekStartOf(date)
  return daysBetween(sunday, addDays(sunday, 6))
}

/**
 * What the strip shows: the week, or in 3 days the three days themselves,
 * spread across the width over their columns (Aaron, note 3.4: "retain the
 * same header, just spread out more to do three days instead of 7").
 */
export function stripDays(view: PhoneCalendarView, date: string): string[] {
  return view === 'three' ? phoneDays('three', date) : stripWeek(date)
}

/** Where a swipe on the strip goes: a week either way, or three days in 3 days. */
export function stripStep(view: PhoneCalendarView): number {
  return view === 'three' ? 3 : 7
}

/** One request for what the strip and the grid need: the strip's days and the days shown. */
export function phoneRange(view: PhoneCalendarView, date: string): { from: string; to: string } {
  const days = [...stripDays(view, date), ...phoneDays(view, date)].sort()
  return { from: days[0]!, to: days[days.length - 1]! }
}

// ---------------------------------------------------------------------------
// Which way a finger went
// ---------------------------------------------------------------------------

/** How far a finger may wander and still be holding still (and below which a move has no direction yet). */
export const SLOP_PX = 8
/** Sideways at least this far is a swipe. */
export const SWIPE_PX = 50

/**
 * The axis a gesture moves on, decided once it has gone past the slop: a
 * finger that sets off sideways is a swipe and one that sets off up or down
 * is a scroll, and it stays that until it is lifted — so a swipe never turns
 * into a scroll halfway through, or the other way round.
 */
export function axisOf(dx: number, dy: number): 'x' | 'y' | null {
  if (Math.hypot(dx, dy) < SLOP_PX) return null
  return Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
}

/** A sideways swipe's direction: 1 forward (the finger went left), -1 back, 0 not a swipe. */
export function swipeOf(dx: number, dy: number): 1 | -1 | 0 {
  if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return 0
  return dx < 0 ? 1 : -1
}

// ---------------------------------------------------------------------------
// Touch and hold to create (note 5)
// ---------------------------------------------------------------------------

/** How long a finger holds still on an empty slot before it is making something (a scroll never waits this long). */
export const HOLD_TO_CREATE_MS = 400
/** What it makes: an hour (note 5: "defaulted to an hour of course"). */
export const NEW_MINUTES = 60
/** The ghost snaps to the quarter hour. */
export const SNAP_MINUTES = 15

const lastStart = DAY_MINUTES - NEW_MINUTES
const clampStart = (minutes: number): number => Math.min(lastStart, Math.max(0, minutes))

/** The slot a finger held: the quarter hour at or before it, the whole hour inside the day. */
export function heldStart(minutes: number): number {
  return clampStart(Math.floor(minutes / SNAP_MINUTES) * SNAP_MINUTES)
}

/**
 * Where the hour is once the finger has moved `dy` pixels from where it held
 * (the grid's own scrolling included): it moves with the finger, snapped to 15
 * minutes, and never leaves the day.
 */
export function draggedStart(held: number, dy: number, scale: number): number {
  return clampStart(held + Math.round(dy / scale / SNAP_MINUTES) * SNAP_MINUTES)
}

/** How close to the top or bottom of the hours a finger must be for them to scroll under it. */
export const EDGE_ZONE_PX = 56
/** The fastest they scroll, in pixels a frame, with the finger right at the edge. */
export const EDGE_SPEED_PX = 12

/**
 * Pixels a frame to scroll the hours while a finger drags a ghost near their
 * edge — negative up, positive down, faster the closer it is — so a slot off
 * the screen can be reached without letting go.
 */
export function edgeScroll(y: number, top: number, bottom: number): number {
  if (y < top + EDGE_ZONE_PX) return -Math.ceil(EDGE_SPEED_PX * Math.min(1, (top + EDGE_ZONE_PX - y) / EDGE_ZONE_PX))
  if (y > bottom - EDGE_ZONE_PX) return Math.ceil(EDGE_SPEED_PX * Math.min(1, (y - (bottom - EDGE_ZONE_PX)) / EDGE_ZONE_PX))
  return 0
}

// ---------------------------------------------------------------------------
// Where the hours open, and how things share a column
// ---------------------------------------------------------------------------

/**
 * Where Day and 3 days open: the desktop's rule (the start of the working day,
 * or earlier when something on the screen is — `openingMinutes`), except that
 * on today, when now is well past what that would show, they open with now in
 * view instead — an hour and a half above it.
 */
export function phoneOpening(opening: number, now: number, isToday: boolean): number {
  if (!isToday || now - opening <= 6 * 60) return opening
  return Math.max(opening, Math.floor((now - 90) / SNAP_MINUTES) * SNAP_MINUTES)
}

/**
 * Overlapping things share a column's width (Outlook's): sorted by start, each
 * takes the first lane free when it starts, and every thing in a run of
 * overlapping ones knows how many lanes the run needed.
 */
export function phoneLanes<T extends { start: number; end: number }>(items: readonly T[]): (T & { lane: number; lanes: number })[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end)
  const out: (T & { lane: number; lanes: number })[] = []
  let run: (T & { lane: number })[] = []
  let ends: number[] = []
  let runEnd = -1
  const close = () => {
    const lanes = Math.max(1, ...run.map((item) => item.lane + 1))
    for (const item of run) out.push({ ...item, lanes })
    run = []
    ends = []
  }
  for (const item of sorted) {
    if (run.length > 0 && item.start >= runEnd) close()
    let lane = ends.findIndex((end) => end <= item.start)
    if (lane === -1) { lane = ends.length; ends.push(item.end) } else ends[lane] = item.end
    run.push({ ...item, lane })
    runEnd = run.length === 1 ? item.end : Math.max(runEnd, item.end)
  }
  if (run.length > 0) close()
  return out
}

// ---------------------------------------------------------------------------
// Holding something to move it (Aaron, Oct 27, note 1.1.4 — Outlook's)
// ---------------------------------------------------------------------------

/**
 * How long a finger holds still on a meeting, a site visit, a time block or a
 * timed task before it lifts — the same wait as making one, so a phone has
 * one touch and hold. A quick tap opens it; a finger that moves first scrolls.
 */
export const HOLD_TO_LIFT_MS = HOLD_TO_CREATE_MS

/** Whether a finger may lift it: the desktop's own rule (`editable` — on it, made it, or may book for someone on it). */
export function liftable(item: { editable: boolean; allDay: boolean }): boolean {
  return item.editable && !item.allDay
}

/** Whether edit mode gives it handles: a timed task is a chip with no length to drag. */
export function hasLength(item: { kind: string }): boolean {
  return item.kind !== 'task'
}

/** A time on the quarter hours. */
export const toQuarter = (minutes: number): number => Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES

/**
 * Where a lifted thing starts once the finger has moved `dy` pixels (the
 * grid's own scrolling included): where it was until the finger has gone half
 * a quarter hour, so lifting never nudges it, then on the quarter hours, the
 * whole of it inside the day.
 */
export function liftedStart(from: number, dy: number, scale: number, minutes: number): number {
  const moved = dy / scale
  if (Math.abs(moved) < SNAP_MINUTES / 2) return from
  return Math.min(DAY_MINUTES - minutes, Math.max(0, toQuarter(from + moved)))
}

/**
 * A handle dragged `dy` pixels in edit mode (answer 6: "dragging the length
 * when it's in edit mode"): the top one moves the start, the bottom one the
 * end, on the quarter hours, never shorter than a quarter hour and never off
 * the day; nothing changes until the finger has gone half a quarter hour.
 */
export function draggedEdge(
  span: { start: number; end: number }, edge: 'start' | 'end', dy: number, scale: number,
): { start: number; end: number } {
  const moved = dy / scale
  if (Math.abs(moved) < SNAP_MINUTES / 2) return span
  if (edge === 'end') {
    return { start: span.start, end: Math.min(DAY_MINUTES, Math.max(span.start + SNAP_MINUTES, toQuarter(span.end + moved))) }
  }
  return { start: Math.max(0, Math.min(span.end - SNAP_MINUTES, toQuarter(span.start + moved))), end: span.end }
}

/** A handle is a finger's 44 px, whatever the 12 px dot it draws. */
export const HANDLE_PX = 44

/**
 * Where the two handles' 44 px go on a block `height` pixels tall at `top`:
 * each reaches out from its own edge and in no further than half the block,
 * so a quarter hour's two never overlap and the finger always gets the one
 * it is on.
 */
export function handleBoxes(top: number, height: number): { start: { top: number; height: number }; end: { top: number; height: number } } {
  const inward = Math.min(14, Math.max(0, height / 2))
  return {
    start: { top: top - (HANDLE_PX - inward), height: HANDLE_PX },
    end: { top: top + height - inward, height: HANDLE_PX },
  }
}

/** Where a lifted thing is going: a column's day and whose column it is. */
export interface LiftPlace { date: string; personId: string }

/**
 * What letting go (or **Done**) writes, through the desktop's own moves —
 * `null` when it is where it was. `minutes` only when its length changed;
 * `personId` only when it went to someone else's column; `verb` is the
 * question a repeat asks (*Move …?* or *Change …?*).
 */
export function liftPlan(
  item: { date: string; start: number; end: number }, from: LiftPlace, to: LiftPlace, span: { start: number; end: number },
): { date: string; start: number; minutes: number | null; personId: string | null; verb: 'move' | 'resize' } | null {
  const elsewhere = to.date !== from.date || to.personId !== from.personId
  const length = span.end - span.start
  const resized = length !== item.end - item.start
  if (!elsewhere && !resized && span.start === item.start) return null
  return {
    date: to.date,
    start: span.start,
    minutes: resized ? length : null,
    personId: to.personId !== from.personId ? to.personId : null,
    verb: resized && !elsewhere ? 'resize' : 'move',
  }
}
